import React from 'react';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { LOCATION } from 'src/hooks/filters/constants';
import {
  PATIENT_JOURNEY_INDEX_ALLOWED_TYPES,
  PATIENT_JOURNEY_TARGET_ALLOWED_TYPES,
} from 'src/components/DynamicCriteriaGenerator/constants';
import { StrategyListGenerator } from '../StrategyListGenerator';

interface PatientJourneyConfigurationProps {
  direction?: 'BEFORE' | 'AFTER';
  onDirectionChange?: (direction: 'BEFORE' | 'AFTER') => void;
  errors?: {
    target_event_interval?: string;
  };
  onClearError?: () => void;
}

const PatientJourneyConfiguration: React.FC<
  PatientJourneyConfigurationProps
> = ({ direction = 'AFTER', onDirectionChange, errors, onClearError }) => {
  const { patientJourneyData, updatePatientJourneyData } =
    useStrategyForm('engage');

  const [localIntervalError, setLocalIntervalError] = React.useState<string | undefined>();
  const intervalError = errors?.target_event_interval || localIntervalError;

  // Normalize direction to handle numeric API values (1=BEFORE, 2=AFTER) or missing prop
  const normalizedDirection: 'BEFORE' | 'AFTER' =
    ((direction as any) === 1 || direction === 'BEFORE') ? 'BEFORE' : 'AFTER';

  // Helper function to safely convert direction to lowercase string for display in sentences
  // Handles both string ('BEFORE'/'AFTER') and number (1/2) values
  const handleDirectionText = (value: any): string => {
    if (value === 1 || value === 'BEFORE') return 'before';
    if (value === 2 || value === 'AFTER') return 'after';
    if (typeof value === 'string') return value.toLowerCase();
    return 'after'; // safe default
  };

  // Timeframe input handling
  const handleTimeframeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onClearError?.();
    setLocalIntervalError(undefined);
    const value = e.target.value;
    if (value === '') {
      updatePatientJourneyData({ target_event_interval: '' as any });
    } else if (/^\d+$/.test(value)) {
      // Dispatch the raw value (including 0) so the input stays in sync with Redux
      updatePatientJourneyData({ target_event_interval: parseInt(value) });
    }
  };

  // Validate on blur without auto-correcting — let the user see and fix the error
  const handleTimeframeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = parseInt(raw);
    if (!raw.trim() || isNaN(parsed) || parsed < 1) {
      setLocalIntervalError('Please enter a valid time frame (minimum 1 month).');
    } else {
      setLocalIntervalError(undefined);
      updatePatientJourneyData({ target_event_interval: parsed });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Index Event Section */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-slate-700">
            Index Event
          </h4>
          <span className="text-xs text-slate-500">
            (Main event being analyzed)
          </span>
        </div>
        <StrategyListGenerator
          location={LOCATION.PATIENT_JOURNEY_INDEX_EVENT}
          allowedTypes={PATIENT_JOURNEY_INDEX_ALLOWED_TYPES}
          strategyType="PATIENT_JOURNEY"
        />
      </div>

      {/* Target Event Timing */}
      <div className="flex flex-col gap-2" data-testid="patient-journey-timing">
        <label className="text-sm font-semibold text-slate-700">
          Target Event Timing
        </label>
        <div className="flex gap-3 items-start">
          {/* Timeframe Input */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={patientJourneyData?.target_event_interval ?? 6}
                onChange={handleTimeframeChange}
                onBlur={handleTimeframeBlur}
                data-testid="target-event-interval-input"
                aria-label="Target event interval in months"
                aria-invalid={!!intervalError}
                aria-describedby={intervalError ? 'interval-error' : undefined}
                className={`w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white text-center font-medium ${
                  intervalError
                    ? 'border-red-500 ring-1 ring-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-slate-300 focus:ring-sky-500 focus:border-sky-500'
                }`}
              />
              <span className="text-sm text-slate-600">months</span>
            </div>
            {intervalError && (
              <p
                id="interval-error"
                className="text-xs text-red-600"
                role="alert"
                data-testid="interval-error-message"
              >
                {intervalError}
              </p>
            )}
          </div>

          {/* Direction Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { onClearError?.(); onDirectionChange?.('BEFORE'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                normalizedDirection === 'BEFORE'
                  ? 'bg-sky-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              aria-pressed={normalizedDirection === 'BEFORE'}
            >
              Before
            </button>
            <button
              type="button"
              onClick={() => { onClearError?.(); onDirectionChange?.('AFTER'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                normalizedDirection === 'AFTER'
                  ? 'bg-sky-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              aria-pressed={normalizedDirection === 'AFTER'}
            >
              After
            </button>
          </div>
        </div>
        <span className="text-sm text-slate-500">
          Target event occurred {patientJourneyData?.target_event_interval ?? 6}{' '}
          months {handleDirectionText(normalizedDirection)} index event
        </span>
      </div>

      {/* Target Event Section */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-slate-700">
            Target Event
          </h4>
          <span className="text-xs text-slate-500">
            (Event that occurred {handleDirectionText(normalizedDirection)} the index event)
          </span>
        </div>
        <StrategyListGenerator
          location={LOCATION.PATIENT_JOURNEY_TARGET_EVENT}
          allowedTypes={PATIENT_JOURNEY_TARGET_ALLOWED_TYPES}
          strategyType="PATIENT_JOURNEY"
        />
      </div>
    </div>
  );
};

export default PatientJourneyConfiguration;
